import { api } from "@/lib/api/client";
import type {
  CommunityAggregateResponse,
  CommunityDetailResponse,
  CommunityInterest,
  CommunityListResponse,
  ContactDetailResponse,
  ContactMessage,
  ContactsListResponse,
  InquiriesListResponse,
  InquiryDetailResponse,
} from "./types";

interface BaseFilters {
  q?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}

interface CommunityFilters {
  q?: string;
  onlyLogged?: boolean;
  onlyAnon?: boolean;
  page?: number;
  pageSize?: number;
}

function build(params: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "" || v === false || v === null) continue;
    out[k] = v === true ? "1" : String(v);
  }
  return out;
}

// ── Producer Inquiries (read-only) ───────────────────────────────────
export const inquiriesApi = {
  list: async (filters: BaseFilters): Promise<InquiriesListResponse> => {
    const { data } = await api.get<InquiriesListResponse>("/admin/inquiries", {
      params: build({ ...filters }),
    });
    return data;
  },
  detail: async (id: number): Promise<InquiryDetailResponse> => {
    const { data } = await api.get<InquiryDetailResponse>(`/admin/inquiries/${id}`);
    return data;
  },
};

// ── Contact Messages ──────────────────────────────────────────────────
export const contactsApi = {
  list: async (filters: BaseFilters): Promise<ContactsListResponse> => {
    const { data } = await api.get<ContactsListResponse>("/admin/contact-messages", {
      params: build({ ...filters }),
    });
    return data;
  },
  detail: async (id: number): Promise<ContactMessage> => {
    const { data } = await api.get<ContactDetailResponse>(`/admin/contact-messages/${id}`);
    return data.contact;
  },
  update: async (
    id: number,
    body: { status?: string; internalNotes?: string | null },
  ): Promise<ContactMessage> => {
    const { data } = await api.patch<ContactDetailResponse>(
      `/admin/contact-messages/${id}`,
      body,
    );
    return data.contact;
  },
};

// ── Community ─────────────────────────────────────────────────────────
export const communityApi = {
  list: async (filters: CommunityFilters): Promise<CommunityListResponse> => {
    const { data } = await api.get<CommunityListResponse>(
      "/admin/community/interests",
      { params: build({ ...filters }) },
    );
    return data;
  },
  detail: async (id: number): Promise<CommunityInterest> => {
    const { data } = await api.get<CommunityDetailResponse>(
      `/admin/community/interests/${id}`,
    );
    return data.interest;
  },
  aggregate: async (): Promise<CommunityAggregateResponse> => {
    const { data } = await api.get<CommunityAggregateResponse>(
      "/admin/community/interests/aggregate",
    );
    return data;
  },
};
